import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles, Loader2, Square, Wand2 } from 'lucide-react';
import { Button } from './ui/button';
import { adminTyreFitmentsAPI } from '../services/api';

const TyreFitmentAiCard = ({ onChanged, onMessage }) => {
  const [status, setStatus] = useState(null);
  const [brand, setBrand] = useState('');
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const load = useCallback(() => adminTyreFitmentsAPI.aiStatus().then(setStatus).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const running = status?.job?.status === 'running';
  useEffect(() => {
    if (!running) return undefined;
    timer.current = setInterval(async () => { const s = await adminTyreFitmentsAPI.aiStatus().catch(() => null); if (s) { setStatus(s); if (s.job?.status !== 'running') onChanged?.(); } }, 3000);
    return () => clearInterval(timer.current);
  }, [running, onChanged]);

  if (!status) return null;
  const job = status.job;
  const sel = brand ? status.brands.find((b) => b.brand === brand) : null;
  const missing = sel ? sel.total - sel.covered : status.brands.reduce((n, b) => n + b.total - b.covered, 0);

  const start = async () => {
    if (!window.confirm(`Générer par IA les dimensions de ${missing} véhicule(s) ${brand ? `de la marque ${brand}` : 'toutes marques'} ? Chaque véhicule consomme un appel IA (~10 à 30 s, traitement en arrière-plan).`)) return;
    setBusy(true);
    try { await adminTyreFitmentsAPI.aiGenerate(brand || null, brand ? 2000 : 500); onMessage?.('Génération IA lancée en arrière-plan'); await load(); } catch (e) { onMessage?.(e.response?.data?.detail || 'Lancement impossible', 'error'); } finally { setBusy(false); }
  };
  const cancel = async () => { await adminTyreFitmentsAPI.aiCancel(); onMessage?.('Génération arrêtée'); load(); onChanged?.(); };
  const toggleOnDemand = async () => {
    const r = await adminTyreFitmentsAPI.aiSettings(!status.on_demand);
    setStatus((s) => ({ ...s, on_demand: r.on_demand }));
    onMessage?.(r.on_demand ? 'Génération à la demande activée : un client qui sélectionne un véhicule sans fiche déclenche la génération.' : 'Génération à la demande désactivée.');
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 mb-6 p-4" data-testid="tyre-ai-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-gray-900 flex items-center gap-2"><Sparkles size={16} className="text-purple-600" /> Dimensions par IA</p>
          <p className="text-xs text-gray-500 mt-1">L'IA (GPT) déduit les montes d'origine (pneus, jantes, entraxe, alésage) des véhicules de la base. Les fiches générées sont marquées « IA – à vérifier » jusqu'à validation.</p>
        </div>
        <button type="button" onClick={toggleOnDemand} data-testid="tyre-ai-on-demand-toggle" disabled={!status.ai_available}
          className={`inline-flex items-center gap-1.5 text-xs rounded-full border px-2.5 py-1 ${status.on_demand ? 'bg-green-50 border-green-200 text-green-800' : 'bg-gray-100 border-gray-300 text-gray-600'}`}>
          <Wand2 size={12} /> À la demande (côté client) : <strong data-testid="tyre-ai-on-demand-value">{status.on_demand ? 'activé' : 'désactivé'}</strong>
        </button>
      </div>
      {!status.ai_available && <p className="mt-3 text-xs text-red-600">Clé IA absente : la génération est indisponible.</p>}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <select value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="tyre-ai-brand" className="h-9 border rounded-md px-2 text-sm bg-white min-w-[220px]" disabled={running}>
          <option value="">Toutes les marques ({status.brands.reduce((n, b) => n + b.total - b.covered, 0)} véhicules sans fiche)</option>
          {status.brands.map((b) => <option key={b.brand} value={b.brand}>{b.brand} — {b.covered}/{b.total} couverts</option>)}
        </select>
        {running ? (
          <Button variant="outline" onClick={cancel} data-testid="tyre-ai-cancel" className="border-red-300 text-red-700 hover:bg-red-50"><Square size={14} className="mr-1" /> Arrêter</Button>
        ) : (
          <Button onClick={start} disabled={busy || !status.ai_available || missing === 0} data-testid="tyre-ai-generate" className="bg-purple-600 hover:bg-purple-700">
            {busy ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Sparkles size={16} className="mr-1" />} Générer les {missing} fiche(s) manquante(s){!brand && missing > 500 ? ' (500 max par lot)' : ''}
          </Button>
        )}
        {status.unverified > 0 && <span className="text-xs text-amber-700" data-testid="tyre-ai-unverified">{status.unverified} fiche(s) IA à vérifier</span>}
      </div>
      {job && (
        <div className="mt-3" data-testid="tyre-ai-job">
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>{job.status === 'running' ? <span className="inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> En cours{job.last ? ` — ${job.last}` : ''}</span> : job.status === 'done' ? 'Terminé' : 'Arrêté'}{job.brand ? ` · ${job.brand}` : ' · toutes marques'}</span>
            <span>{job.done} / {job.total} · {job.created} fiche(s) créée(s){job.failed ? ` · ${job.failed} échec(s)` : ''}</span>
          </div>
          <div className="h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-purple-500 transition-all" style={{ width: `${job.total ? Math.round((job.done / job.total) * 100) : 0}%` }} /></div>
        </div>
      )}
    </div>
  );
};

export default TyreFitmentAiCard;
